// src/Dashboard/pages/user/Absences/hooks/useAbsences.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAdherentsByUser,
  updateBlessures,
} from "../../../../../services/adherentServices";

import {
  buildAbsenceNote,
  buildReturnNote,
  DEFAULT_ABSENCE_FORM,
  getCurrentUserId,
  getInputDateFromApiDate,
  hasAbsenceData,
  isAdherentAbsent,
} from "../utils/absencesUtils";

export function useAbsences() {
  const userId = getCurrentUserId();

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");

  const [form, setForm] = useState(DEFAULT_ABSENCE_FORM);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);

  const selectedAdherent = useMemo(() => {
    return (
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId)
      ) || null
    );
  }, [adherents, selectedAdherentId]);

  const updateForm = useCallback((patch) => {
    setForm((previous) => ({
      ...previous,
      ...patch,
    }));
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError("Utilisateur non identifié.");
      toast.error("Utilisateur non identifié.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = await getAdherentsByUser(userId);
      const list = Array.isArray(data) ? data : [];

      setAdherents(list);

      setSelectedAdherentId((previous) => {
        if (previous && list.some((adherent) => String(adherent.id) === String(previous))) {
          return previous;
        }

        return list[0]?.id ? String(list[0].id) : "";
      });
    } catch (err) {
      console.error("Erreur récupération adhérents :", err);
      setError("Impossible de charger vos adhérents pour le moment.");
      toast.error("Impossible de charger vos adhérents.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selectedAdherent) {
      setForm(DEFAULT_ABSENCE_FORM);
      return;
    }

    setForm({
      isAbsent: !!selectedAdherent.is_blessed,
      returnDate: getInputDateFromApiDate(selectedAdherent.date_reprise),
      reason: "",
    });
  }, [selectedAdherent]);

  const resetForm = useCallback(() => {
    if (!selectedAdherent) {
      setForm(DEFAULT_ABSENCE_FORM);
      return;
    }

    setForm({
      isAbsent: !!selectedAdherent.is_blessed,
      returnDate: getInputDateFromApiDate(selectedAdherent.date_reprise),
      reason: "",
    });
  }, [selectedAdherent]);

  const updateAdherentLocally = useCallback((adherentId, patch) => {
    setAdherents((previous) =>
      previous.map((adherent) =>
        adherent.id === adherentId
          ? {
              ...adherent,
              ...patch,
            }
          : adherent
      )
    );
  }, []);

  const saveAbsence = useCallback(
    async (event) => {
      event?.preventDefault?.();

      if (!selectedAdherent) {
        toast.warn("Choisissez d’abord un adhérent.");
        return;
      }

      if (!hasAbsenceData(form)) {
        toast.warn("Merci de préciser une absence, une date de reprise ou un motif.");
        return;
      }

      const note = buildAbsenceNote({
        returnDate: form.returnDate,
        reason: form.reason,
      });

      const newNotes = selectedAdherent.notes_adherent
        ? `${selectedAdherent.notes_adherent} | ${note}`
        : note;

      try {
        setSaving(true);

        await updateBlessures(selectedAdherent.id, {
          is_blessed: !!form.isAbsent,
          date_reprise: form.returnDate || null,
          notes_adherent: newNotes,
        });

        updateAdherentLocally(selectedAdherent.id, {
          is_blessed: !!form.isAbsent,
          date_reprise: form.returnDate || null,
          notes_adherent: newNotes,
        });

        toast.success("Absence enregistrée avec succès.");
      } catch (err) {
        console.error("Erreur enregistrement absence :", err);
        toast.error("Erreur lors de l’enregistrement de l’absence.");
      } finally {
        setSaving(false);
      }
    },
    [form, selectedAdherent, updateAdherentLocally]
  );

  const markReturned = useCallback(async () => {
    if (!selectedAdherent) return;

    const canMarkReturned = isAdherentAbsent(selectedAdherent);

    if (!canMarkReturned) {
      toast.info("Cet adhérent n’est pas signalé absent.");
      return;
    }

    const confirmReturn = window.confirm(
      `Confirmer la reprise pour ${selectedAdherent.prenom} ${selectedAdherent.nom} ?`
    );

    if (!confirmReturn) return;

    const note = buildReturnNote();

    const newNotes = selectedAdherent.notes_adherent
      ? `${selectedAdherent.notes_adherent} | ${note}`
      : note;

    try {
      setMarking(true);

      await updateBlessures(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      updateAdherentLocally(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      setForm({
        isAbsent: false,
        returnDate: "",
        reason: "",
      });

      toast.success("Reprise enregistrée.");
    } catch (err) {
      console.error("Erreur reprise adhérent :", err);
      toast.error("Impossible d’enregistrer la reprise.");
    } finally {
      setMarking(false);
    }
  }, [selectedAdherent, updateAdherentLocally]);

  const stats = useMemo(() => {
    const total = adherents.length;
    const absent = adherents.filter((adherent) => !!adherent.is_blessed).length;
    const returnPlanned = adherents.filter(
      (adherent) => !!adherent.date_reprise
    ).length;
    const active = Math.max(0, total - absent);

    return {
      total,
      absent,
      active,
      returnPlanned,
    };
  }, [adherents]);

  return {
    loading,
    error,

    adherents,
    selectedAdherent,
    selectedAdherentId,
    setSelectedAdherentId,

    form,
    updateForm,

    saving,
    marking,

    stats,

    saveAbsence,
    markReturned,
    refresh,
    resetForm,
  };
}